// Types copied from wine-scanner-frontend

export type UUID4 = string;

// --- recommendation.ts ---

export enum MatchType {
    EXACT = "EXACT",
    HIGH = "HIGH",
    MEDIUM = "MEDIUM",
    LOW = "LOW",
}

export type RecommendationMatch = {
    overall_match: MatchType;
    country_match: MatchType;
    region_match: MatchType;
    grape_match: MatchType;
};

// --- navino.ts ---

export interface NavinoGrapes {
    text: string;
    grapes: string[];
}

export interface NavinoWineDescription {
    object_id: string;
    highlight_result?: any;
    snippet_result?: any;
    ranking_info?: any;
    distinct_seq_id?: any;
    name: string;
    nameFr: string;
    nameIt: string;
    description: string;
    descriptionFr: string;
    descriptionIt: string;
    img_url: string;
    category: string;
    categoryFr: string;
    categoryIt: string;
    taste: number;
    sweetness: number;
    region: string;
    regionFr: string;
    regionIt: string;
    country: string;
    countryFr: string;
    countryIt: string;
    year?: number;
    alcoholContent?: number;
    closure: string;
    closureFr: string;
    closureIt: string;
    grapes: NavinoGrapes;
    drinking_temperature?: number[];
    appellation: string;
    ageingContainer: boolean;
    ageingTime?: any;
    producer?: string;
    bioVegan: any[];
    flags: any[];
    outOfStock: boolean;
    nextAvailabilityDate?: string;
    recommendRanking: Record<string, number>;
    nielsenRanking: Record<string, number>;
    discountRanking: number;
    suitableFood: string[];
    suitableFoodFr: string[];
    suitableFoodIt: string[];
    collections: string[];
    collectionsFr: string[];
    collectionsIt: string[];
    awards: any[];
    awardsFr: any[];
    awardsIt: any[];
    saleStart?: string;
    saleEnd?: string;
    sale_price_per_unit: number;
    regular_price_per_unit: number;
    sale_price_per_bottle: number;
    regular_price_per_bottle: number;
    deposit?: number;
    isDiscount: boolean;
    bottlesPerUnit: number;
    delivery_unit: string;
    delivery_unitFr: string;
    delivery_unitIt: string;
    bottle_size: number;
}

// --- wine.ts ---

export type CountryAlpha2 = string;

export enum WineTypes {
    RED = "RED",
    WHITE = "WHITE",
    ROSE = "ROSE",
    SPARKLING = "SPARKLING",
    SPARKLING_ROSE = "SPARKLING_ROSE",
    ORANGE = "ORANGE",
    DESSERT = "DESSERT",
}

export enum PriceUnit {
    GLASS = "GLASS",
    CARAFE = "CARAFE",
    BOTTLE = "BOTTLE",
}

type DecimalString = string;

export type WinePrice = {
    price: DecimalString;
    unit: PriceUnit;
    volume_cl?: DecimalString;
};

export type ScannedWineInfo = {
    wine_listing_id: string;
    name?: string;
    country_code?: CountryAlpha2;
    region?: string;
    producer?: string;
    grapes?: string[];
    price?: WinePrice;
};

export type WineRecommendationItem = {
    navino_wine_description: NavinoWineDescription;
    recommendation_match: RecommendationMatch;
};

export type Recommendation = {
    scanned_wine_info: ScannedWineInfo;
    recommendations: WineRecommendationItem[];
};

export type AnalyzedWineMenuSection = {
    section_type?: WineTypes;
    recommendations: Recommendation[];
};

export type WineMenuScanResult = {
    sections: AnalyzedWineMenuSection[];
};

// --- metadata.ts ---

export type PriceRange = {
    min?: number;
    max?: number;
};

export type WineFilterCriteria = {
    price?: PriceRange;
    countrie_codes?: CountryAlpha2[];
    regions?: string[];
    grapes?: string[];
    flags?: string[];
    volumes_cl?: number[];
};

export type SystemGeneratedPresets = {
    overall_price_range: PriceRange;
    total_wine_count: number;
};

export type WineListingId = string;

export type IndividualWineSettings = {
    wine_listing_id: WineListingId;
    filters?: WineFilterCriteria;
    selected_wine_ids?: string[];
    additional_selected_wine_ids?: string[];
};

export type WineCategory = {
    wine_category_id: string;
    name: string;
    wine_type_key?: string;
    selected_wine_ids: string[];
};

export type GeneratedPdf = {
    name: string;
    url: string;
    created_at: string;
};

export type Metadata = {
    system_presets: SystemGeneratedPresets;
    global_filters: WineFilterCriteria;
    individual_settings: IndividualWineSettings[];
    show_only_matches: boolean;
    generated_pdf?: GeneratedPdf;
    volumes_cl?: Record<WineListingId, number[]>;
    additional_selected_wine_ids?: string[];
    wine_categories?: WineCategory[];
};

// --- job.ts ---

export enum JobStatus {
    SUBMITTED = "SUBMITTED",
    ANALYSIS_IN_PROGRESS = "ANALYSIS_IN_PROGRESS",
    ANALYSIS_COMPLETED = "ANALYSIS_COMPLETED",
    PDF_GENERATED = "PDF_GENERATED",
    FAILED = "FAILED",
}

export enum IssueCode {
    NO_WINES_DETECTED = "NO_WINES_DETECTED",
    PARTIAL_ANALYSIS_FAILURE = "PARTIAL_ANALYSIS_FAILURE",
}

export type NoWinesDetectedIssue = {
    code: IssueCode.NO_WINES_DETECTED;
    page: number;
};

export type PartialAnalysisFailureIssue = {
    code: IssueCode.PARTIAL_ANALYSIS_FAILURE;
    page: number;
    failed_listing_idxs: number[];
};

export type AnyIssue = NoWinesDetectedIssue | PartialAnalysisFailureIssue;

export type JobState = {
    job_status: JobStatus;
    error_msg?: string;
    issues: AnyIssue[];
};

export type ScanJobHistoryItem = {
    job_id: UUID4;
    created_at: string;
    state: JobState;
    customer_id: string;
    customer_name: string;
    total_wines_count?: number;
    recommended_wines_count?: number;
};

export type GetScanJobsResponse = {
    history: ScanJobHistoryItem[];
};

export type GetScanJobResultResponse = {
    scan_result: WineMenuScanResult;
    metadata: Metadata;
    state: JobState;
};
